// Frontend Sentry — initialised at runtime from /api/config/public (Phase 7).
// The DSN is PUBLIC by design (ships in the browser). Graceful: if the DSN is
// absent, initSentry() is a no-op and the app runs normally. Full Replay
// masking is set EXPLICITLY (never relying on implicit defaults) because we
// capture health-adjacent questionnaire answers.

import * as Sentry from '@sentry/react';

let _active = false;

// Benign errors we never want in the feed.
const IGNORE = [
  'Session expired',
  'AbortError',
  'The user aborted a request',
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  'Failed to fetch', // network-cancelled / offline — noisy, low signal
];

export function initSentry({ dsn, environment, release } = {}) {
  if (_active || !dsn) return false;
  try {
    Sentry.init({
      dsn,
      environment: environment || 'development',
      release: release || undefined,
      integrations: [
        Sentry.browserTracingIntegration(),
        // Replay is intentionally NOT in the initial integrations list —
        // it ships ~215 KB raw of SDK. It's loaded lazily via
        // `loadReplayLater()` after the page's `load` event so it never
        // blocks first paint. Error capture itself (this init) stays eager.
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      sendDefaultPii: false,
      ignoreErrors: IGNORE,
      beforeSend(event) {
        // Defensive scrub: strip any Authorization header / token-like fields.
        try {
          if (event.request && event.request.headers) {
            delete event.request.headers.Authorization;
            delete event.request.headers.authorization;
            delete event.request.headers.Cookie;
          }
        } catch {
          /* ignore */
        }
        return event;
      },
    });
    _active = true;
    return true;
  } catch {
    return false;
  }
}

// Fetch and attach the Replay integration after boot. Called from index.js
// on `load` + requestIdleCallback (setTimeout fallback) — see the deferred
// scheduler there. Safe to call multiple times; addIntegration is idempotent
// per integration name.
let _replayLoaded = false;
export async function loadReplayLater() {
  if (!_active || _replayLoaded) return false;
  try {
    // Sentry.lazyLoadIntegration ships the integration as a separate chunk
    // (loaded from the CDN in production) so it doesn't bloat main.js.
    const replayIntegration = await Sentry.lazyLoadIntegration('replayIntegration');
    const client = Sentry.getClient();
    if (!client) return false;
    client.addIntegration(
      replayIntegration({
        // Explicit privacy — health-adjacent data must never be readable.
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      })
    );
    _replayLoaded = true;
    return true;
  } catch {
    return false;
  }
}

export function isActive() {
  return _active;
}

export function setSentryUser(user) {
  if (!_active || !user) return;
  try {
    Sentry.setUser({ id: user.id, email: user.email });
  } catch {
    /* ignore */
  }
}

export function clearSentryUser() {
  if (!_active) return;
  try {
    Sentry.setUser(null);
  } catch {
    /* ignore */
  }
}

// Safe capture — no-op when inactive, never throws into business logic.
export function captureError(error, context = {}) {
  if (!_active) return;
  try {
    Sentry.captureException(error, { tags: context.tags, extra: context.extra });
  } catch {
    /* ignore */
  }
}

export { Sentry };
